import * as React from 'react';
import { GraphqlQuery } from 'requestapijs';
import { Loading, notify } from 'sha-el-design/lib';
import {
  EmailSettingNodeConnection, StateNodeEdge, EmailSettingNode, EmployeeNodeEdge,
} from '../../../schema';
import { Pagination } from '../Common/Pagination';
import { EmployeeDropdown } from '../Common/EmployeeDropdown';
import { StateDropdown } from '../Common/StateDropdown';
import { emailListQuery } from './constants';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import { Inject } from '../../helpers';
import { EmailService } from '../../service/emailService';
import { MdEdit } from 'react-icons/md';
import '../Common/listPageStyles';

/**
 * The recipients configured for the work-completion report.
 *
 * Previously a two-column table of employee NAMES with a red Delete button per
 * row -- on the one screen whose entire purpose is email addresses. It also
 * passed its state filter under the wrong variable name, so the state panel
 * listed every state's recipients regardless of the state selected.
 */
export class EmailEmployeeList extends React.Component<Props, State> {

  @Inject emailService: EmailService;

  constructor(prop: Props) {
    super(prop);
    this.state = {
      after: '', confirmingId: null, busyId: null,
      editingId: null, editEmployee: null, editState: null,
    };
  }

  /**
   * Editing a recipient is ADD-THE-NEW-THEN-REMOVE-THE-OLD, not an update.
   *
   * There is no update path on the server: EmailSettingSerializer.update()
   * deletes the row, and the Delete button depends on exactly that, so making
   * it a real update would break removal. Two writes it is.
   *
   * The order matters and is deliberate: create first, delete second. If the
   * create fails nothing has changed; if the delete fails the recipient list
   * has a duplicate, which is visible and removable. The reverse order could
   * drop a recipient entirely and nobody would notice until a report went to
   * the wrong people.
   */
  saveEdit = (node: EmailSettingNode) => {
    const { editEmployee, editState } = this.state;
    const oldEmployeeId = nestedAccess(node, 'employee', 'id');
    const oldStateId = nestedAccess(node, 'state', 'id');
    const newEmployeeId = nestedAccess(editEmployee, 'node', 'id') || oldEmployeeId;
    const newStateId = this.props.isGlobal
      ? null
      : (nestedAccess(editState, 'node', 'id') || oldStateId);

    // Normalise before comparing. A global row has no state, and the two sides
    // spell that differently -- nestedAccess yields undefined for a null
    // relation while the new value is an explicit null -- so a bare === called
    // every untouched global row "changed" and churned it through a create and
    // a delete for no reason. Caught by the harness printing the mutation
    // sequence and showing the ORIGINAL employee id in the create.
    const same = (a: any, b: any) =>
      ((a === undefined || a === '') ? null : a) === ((b === undefined || b === '') ? null : b);
    if (same(newEmployeeId, oldEmployeeId) && same(newStateId, oldStateId)) {
      return this.cancelEdit();
    }

    const address = nestedAccess(editEmployee, 'node', 'emailid');
    if (editEmployee && (!address || !String(address).trim())) {
      return notify({
        type: 'error',
        title: 'That employee has no email address',
        message: 'Add one to their employee record first, or the report will fail to send.',
        duration: 5000,
      });
    }
    if (this.props.existingEmployeeIds
        && this.props.existingEmployeeIds.indexOf(newEmployeeId) !== -1
        && newEmployeeId !== oldEmployeeId) {
      return notify({
        type: 'error',
        title: 'Already a recipient',
        message: 'That person already receives this report.',
        duration: 4000,
      });
    }

    this.setState({ busyId: node.id });
    this.emailService.add(
      { employee: newEmployeeId, state: newStateId, isGlobal: this.props.isGlobal } as any,
      (created) => {
        const createErrors = nestedAccess(created, 'data', 'createEmailSetting', 'errors');
        if (createErrors && createErrors.length) {
          this.setState({ busyId: null });
          return notify({
            type: 'error',
            title: 'Could not save the change',
            message: createErrors[0].messages[0],
            duration: 4500,
          });
        }
        // New row is in. Now retire the old one.
        this.emailService.add(
          { id: node.id, employee: oldEmployeeId } as any,
          (removed) => {
            const removeErrors = nestedAccess(removed, 'data', 'createEmailSetting', 'errors');
            this.setState({ busyId: null, editingId: null, editEmployee: null, editState: null });
            if (removeErrors && removeErrors.length) {
              notify({
                type: 'error',
                title: 'Saved, but the old entry is still there',
                message: 'Remove it manually — the report would otherwise go out twice.',
                duration: 6000,
              });
            } else {
              notify({ type: 'success', title: 'Recipient updated', duration: 2000 });
            }
            this.props.onChanged();
          },
          () => {
            this.setState({ busyId: null, editingId: null, editEmployee: null, editState: null });
            notify({
              type: 'error',
              title: 'Saved, but the old entry is still there',
              message: 'Remove it manually — the report would otherwise go out twice.',
              duration: 6000,
            });
            this.props.onChanged();
          },
        );
      },
      () => {
        this.setState({ busyId: null });
        notify({ type: 'error', title: 'Could not reach the server', duration: 3500 });
      },
    );
  }

  startEdit = (node: EmailSettingNode) => this.setState({
    editingId: node.id,
    confirmingId: null,
    // Empty: the row shows what it currently is, the pickers say what to
    // change it to. Anything left untouched keeps its existing value (see
    // saveEdit's `|| old...` fallbacks).
    editEmployee: null,
    editState: null,
  })

  cancelEdit = () => this.setState({ editingId: null, editEmployee: null, editState: null })

  initials = (name: string) => (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('')

  // Removal goes through the same createEmailSetting mutation: the serializer's
  // update() deletes the row, and no delete mutation exists. Documented rather
  // than "fixed" here because the backend contract is what it is.
  remove = (node: EmailSettingNode) => {
    this.setState({ busyId: node.id, confirmingId: null });
    this.emailService.add(
      { id: node.id, employee: nestedAccess(node, 'employee', 'id') } as any,
      (response) => {
        const errors = nestedAccess(response, 'data', 'createEmailSetting', 'errors');
        this.setState({ busyId: null });
        if (errors && errors.length) {
          return notify({
            type: 'error',
            title: 'Could not remove',
            message: errors[0].messages[0],
            duration: 4000,
          });
        }
        notify({ type: 'success', title: 'Recipient removed', duration: 2000 });
        // Refetch in place. This used to be
        // `setTimeout(() => window.location.href = ..., 1000)` -- a full page
        // reload, a second later, losing everything else on screen.
        this.props.onChanged();
      },
      () => {
        this.setState({ busyId: null });
        notify({ type: 'error', title: 'Could not reach the server', duration: 3500 });
      },
    );
  }

  row = (node: EmailSettingNode) => {
    const name = nestedAccess(node, 'employee', 'name') || 'Unknown employee';
    const raw = nestedAccess(node, 'employee', 'emailid');
    const address = raw ? String(raw) : '';
    const trimmed = address.trim();
    // A stored address with surrounding whitespace, or none at all, is
    // invisible until the send fails. Production has one of each shape.
    const problem = !trimmed
      ? 'no address'
      : trimmed !== address ? 'has spaces'
      : trimmed.indexOf('@') === -1 ? 'not an address' : null;
    const confirming = this.state.confirmingId === node.id;
    const editing = this.state.editingId === node.id;
    const busy = this.state.busyId === node.id;

    return (
      <div
        className={
          'cs-mail__row' +
          (confirming ? ' cs-mail__row--confirming' : '') +
          (editing ? ' cs-mail__row--editing' : '')
        }
        key={node.id}
      >
        <span className='cs-mail__avatar' aria-hidden='true'>{this.initials(name)}</span>
        {editing ? (
          <>
            {/* The pickers start EMPTY rather than pre-filled with the current
                values. Editing here means "send this to someone else instead",
                so an empty "Replace with" field states the action plainly --
                and a pre-filled sha-el AutoComplete will not reopen its menu
                once it holds a value, which made changing the selection
                awkward for a user and impossible to drive in a test. The
                current values stay visible as text beside the pickers. */}
            <div className='cs-mail__edit'>
              <span className='cs-mail__edit-current'>
                Replace <b>{name}</b>
                {!this.props.isGlobal && nestedAccess(node, 'state', 'name')
                  ? <> ({nestedAccess(node, 'state', 'name')})</> : ''}
                {' '}with
              </span>
              <div className='cs-mail__edit-field'>
                <EmployeeDropdown
                  value={this.state.editEmployee}
                  inputProps={{}}
                  uniqueIdentifier={(v) => v.node.id}
                  displayProp={(v) => v && v.node.name}
                  onChange={(v) => this.setState({ editEmployee: v })}
                  label='Another employee'
                  mode='single'
                />
              </div>
              {!this.props.isGlobal && (
                <div className='cs-mail__edit-field'>
                  <StateDropdown
                    mode='single'
                    value={this.state.editState}
                    onChange={(v) => this.setState({ editState: v })}
                    uniqueIdentifier={(v) => v && v.node.id}
                    displayProp={(v) => (v && v.node.name) || ''}
                    label='Or another state'
                  />
                </div>
              )}
              <div className='cs-mail__edit-actions'>
                <button
                  type='button'
                  className='cs-chip cs-chip--save'
                  // Nothing picked means nothing to save.
                  disabled={busy || (!this.state.editEmployee && !this.state.editState)}
                  onClick={() => this.saveEdit(node)}
                >
                  {busy ? 'Saving...' : 'Save'}
                </button>
                <button
                  type='button'
                  className='cs-chip'
                  disabled={busy}
                  onClick={this.cancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          </>
        ) : confirming ? (
          <div className='cs-mail__confirm'>
            <span className='cs-mail__confirm-text'>
              Stop sending the report to <b>{name}</b>?
            </span>
            <button
              type='button'
              className='cs-chip cs-chip--danger'
              onClick={() => this.remove(node)}
            >
              Remove
            </button>
            <button
              type='button'
              className='cs-chip'
              onClick={() => this.setState({ confirmingId: null })}
            >
              Keep
            </button>
          </div>
        ) : (
          <>
            <div className='cs-mail__who'>
              <p className='cs-mail__name'>{name}</p>
              <p className={`cs-mail__addr${problem ? ' cs-mail__addr--bad' : ''}`}>
                {trimmed || 'No email address on this employee'}
              </p>
            </div>
            {problem && <span className='cs-mail__flag'>{problem}</span>}
            <button
              type='button'
              className='cs-mail__remove cs-mail__rowedit'
              aria-label={`Edit ${name}`}
              title='Change this recipient'
              disabled={busy}
              onClick={() => this.startEdit(node)}
            >
              <MdEdit />
            </button>
            <button
              type='button'
              className='cs-mail__remove'
              aria-label={`Remove ${name}`}
              title='Remove recipient'
              disabled={busy}
              onClick={() => this.setState({ confirmingId: node.id, editingId: null })}
            >
              ×
            </button>
          </>
        )}
      </div>
    );
  }

  // Report the employees currently on this list up to the page, so it can
  // refuse a duplicate before writing one. Scheduled out of the render pass
  // (the render callback belongs to GraphqlQuery) and only when the set
  // actually changed, so it cannot loop.
  private lastReported: string = null;

  reportLoaded = (edges: any[]) => {
    if (!this.props.onLoaded) { return; }
    const ids = edges
      .map((e) => nestedAccess(e, 'node', 'employee', 'id'))
      .filter(Boolean);
    const key = ids.join('|');
    if (key === this.lastReported) { return; }
    this.lastReported = key;
    setTimeout(() => this.props.onLoaded(ids), 0);
  }

  render() {
    const { isGlobal, state } = this.props;
    const stateId = nestedAccess(state, 'node', 'id');
    return (
      <GraphqlQuery
        // refreshKey changes after every add/remove, which is what makes the
        // list refetch without the old full-page reload.
        key={`${isGlobal}|${stateId || ''}|${this.props.refreshKey}`}
        queryString={emailListQuery}
        variables={{
          isGlobal,
          // Was passed as `stateId`, but the query declares `$state` -- so the
          // filter was never applied and the state panel listed every state's
          // recipients at once.
          state: stateId,
          after: this.state.after,
        }}
        render={(response: { allEmailSettings: EmailSettingNodeConnection }, error, loading) => {
          const edges = nestedAccess(response, 'allEmailSettings', 'edges');
          if (error || (!loading && !edges)) {
            return <div className='cs-mail__empty'>Could not load the recipients.</div>;
          }
          if (!loading) { this.reportLoaded(edges); }
          return (
            <Loading
              isLoading={loading}
              render={() => (
                <>
                  {edges.length ? (
                    <div className='cs-mail__list'>
                      {edges.map((e) => this.row(e.node))}
                    </div>
                  ) : (
                    <div className='cs-mail__empty'>
                      {isGlobal
                        ? 'Nobody receives every report yet.'
                        : stateId
                          ? 'Nobody receives reports for this state yet.'
                          : 'Pick a state to see who receives its reports.'}
                    </div>
                  )}
                  {nestedAccess(response, 'allEmailSettings', 'pageInfo', 'hasNextPage') && (
                    <Pagination
                      endCursor={nestedAccess(response, 'allEmailSettings', 'pageInfo', 'endCursor')}
                      hasNextPage={true}
                      after={(after) => this.setState({ after })}
                    />
                  )}
                </>
              )}
            />
          );
        }}
      />
    );
  }
}

interface Props {
  state?: StateNodeEdge;
  isGlobal: boolean;
  refreshKey: number;
  onChanged: () => void;
  /** employee ids currently on this list, so the page can block duplicates */
  onLoaded?: (employeeIds: string[]) => void;
  /** the same ids handed back down, so an edit cannot create a duplicate */
  existingEmployeeIds?: string[];
}

interface State {
  after: string;
  confirmingId: string;
  busyId: string;
  editingId: string;
  editEmployee: EmployeeNodeEdge;
  editState: StateNodeEdge;
}
